import { Injectable } from '@angular/core';
import { ModuleManagerExportFormat } from 'src/shared/models/module-manager/module-manager-options.model';
import { ApiResponse } from 'src/shared/models/api-response.model';
import { CVApiService } from 'src/app/reusables/services/cv-api.service';
import { EvaluationsData, QualityScoreData, ChannelsData, RawChannelsData } from './quality-score.model';
import { BehaviorSubject } from 'rxjs';
import evaluationsData from './quality-score-evaluations-data.json';

@Injectable({
    providedIn: 'root'
})
export class QualityScoreService {
    private stateSubject = new BehaviorSubject<any>({});
    public state$ = this.stateSubject.asObservable();

    constructor(
        private cvApiService: CVApiService
    ) {
    }

    setState(key: string, value: any) {
        const currentState = this.stateSubject.value;
        currentState[key] = value;
        this.stateSubject.next(currentState);
    }

    public async LoadQualityScoreData(moduleKey: string, isAutoRefresh: boolean = false, isFirstLoad: boolean = false): Promise<QualityScoreData> {
        let renderEndpoint: string;
        try {
            renderEndpoint = await this.cvApiService.getModuleRenderEndPoint(moduleKey);
        } catch (err) {
            console.error("Failed getting module render endpoint");
        }

        try {
            const url = `/api/v2/dashboards/modules/qualityscore/${moduleKey}?isAutoRefresh=${isAutoRefresh}&isFirstLoad=${isFirstLoad}`;
            const response = await this.cvApiService.get<QualityScoreData>(url, renderEndpoint);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        } catch (err) {
            console.error("Failed getting data for Quality Score module");
            throw err;
        }
    }

    public async LoadQualityScoreEvaluationData(moduleKey: string, isAutoRefresh: boolean = false, isFirstLoad: boolean = false): Promise<EvaluationsData[]> {
        let renderEndpoint: string;
        try {
            renderEndpoint = await this.cvApiService.getModuleRenderEndPoint(moduleKey);
        } catch (err) {
            console.error("Failed getting module render endpoint");
            throw err;
        }
        try {
            const url = `/api/v2/dashboards/modules/qualityscore/${moduleKey}/evaluations?isAutoRefresh=${isAutoRefresh}&isFirstLoad=${isFirstLoad}`;
            const response = await this.cvApiService.get<EvaluationsData[]>(url, renderEndpoint);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        } catch (err) {
            console.error("Failed getting data for Quality Score module Evaluations view");
            throw err;
        }
    }

    public async LoadQualityScoreChannelsData(moduleKey: string, isAutoRefresh: boolean = false, isFirstLoad: boolean = false): Promise<ChannelsData[]> {
        let renderEndpoint: string;
        try {
            renderEndpoint = await this.cvApiService.getModuleRenderEndPoint(moduleKey);
        } catch (err) {
            console.error("Failed getting module render endpoint");
            throw err;
        }
        try {
            const apiData = {
                "status": "success",
                "message": "OK",
                "execution_time_utc": "2025-04-11T06:54:15.7648659Z",
                "warnings": [],
                "status_code": 200,
                "data": evaluationsData.ChannelsData,
                "test_data": {}
            };
            const response = await Promise.resolve(apiData);
            if (response.status_code >= 200 && response.status_code < 300) {
                const allChannels = Array.from(
                    new Set(response.data.flatMap(({ Channels }) => Channels.map(({ Channel }) => Channel).filter(Boolean)))
                );

                const responseData = response.data.map(({ Team, Agent, AgentID, TeamAvgScore, Channels }) => {
                    const channelScores = Object.fromEntries(
                        Channels.filter(({ Channel }) => Channel).map(({ Channel, AvgScore }) => [Channel, AvgScore])
                    );

                    allChannels.forEach(channel => {
                        if (!(channel in channelScores)) {
                            channelScores[channel] = null;
                        }
                    });

                    return {
                        Team,
                        Agent,
                        AgentID,
                        ...channelScores
                    };
                });
                return responseData;
            } else throw response;
        } catch (err) {
            console.error("Failed getting data for Quality Score module Channels view");
            throw err;
        }
    }

    public async GetQualityScoreDataForExport(moduleKey: string, format: ModuleManagerExportFormat = ModuleManagerExportFormat.Excel, isCXOne: boolean): Promise<ApiResponse<string>> {
        let renderEndpoint: string;
        try {
            renderEndpoint = await this.cvApiService.getModuleRenderEndPoint(moduleKey);
        } catch (err) {
            console.error("Failed getting module render endpoint");
        }

        try {
            const url = `/api/v2/dashboards/modules/qualityscore/${encodeURIComponent(moduleKey)}/export?format=${encodeURIComponent(format)}` + `&isCXOne=${isCXOne}`;
            const response = await this.cvApiService.get<string>(url, renderEndpoint);
            if (response.status_code >= 200 && response.status_code < 300)
                return response;
            else throw response;
        } catch (err) {
            console.error("Failed getting data to export for Quality Score module");
            throw err;
        }
    }
}
